import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  CircularProgress
} from "@mui/material";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";

import { getCapacityOverview } from "../../services/reportService";
import { initialsOf } from "../../utils/commentUtils";
import { avatarColorOf } from "../../utils/avatarUtils";
import uiConfig from "../../config/uiConfig";

const PALETTE = uiConfig.chartPalette;
const BUCKET_COLORS = {
  "0-25%": PALETTE[0],
  "26-50%": PALETTE[1],
  "51-75%": PALETTE[2],
  "76-100%": PALETTE[3],
  "Over 100%": PALETTE[4]
};

function EmployeeRow({ employee }) {
  const overallocated = employee.total_capacity_percent > 100;

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.8 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: avatarColorOf(employee.employee_name) }}>
          {initialsOf(employee.employee_name)}
        </Avatar>
        <Typography sx={{ fontSize: 13, color: "text.primary" }}>{employee.employee_name}</Typography>
      </Box>

      <Chip
        size="small"
        label={`${employee.total_capacity_percent}%`}
        sx={{
          fontWeight: 700,
          fontSize: 11,
          background: overallocated ? "#fee2e2" : "#eff6ff",
          color: overallocated ? "#dc2626" : "#2563eb"
        }}
      />
    </Box>
  );
}

function CapacityOverviewReport({ departmentId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    getCapacityOverview(departmentId)
      .then(setData)
      .catch((error) => console.error("Capacity Overview Error:", error))
      .finally(() => setLoading(false));
  }, [departmentId]);

  if (loading) {
    return (
      <Box height={330} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  const distribution = data?.distribution || [];
  const topAllocated = data?.top_allocated || [];
  const bench = data?.bench || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>
            Capacity &amp; Resource Overview
          </Typography>

          <Chip
            size="small"
            label={`${data?.bench_count || 0} on bench`}
            sx={{ fontWeight: 700, background: "#fef3c7", color: "#b45309" }}
          />
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", mb: 1 }}>
          Capacity Distribution
        </Typography>

        <ResponsiveContainer width="100%" height={230}>
          <BarChart data={distribution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" fontSize={11} />
            <YAxis allowDecimals={false} fontSize={12} />
            <Tooltip />
            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
              {distribution.map((entry) => (
                <Cell key={entry.name} fill={BUCKET_COLORS[entry.name] || PALETTE[5]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2
        }}
      >
        <Paper
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mb: 1.5 }}>
            Top Allocated
          </Typography>

          {topAllocated.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>No data</Typography>
          )}

          {topAllocated.map((employee) => (
            <EmployeeRow key={employee.employee_id} employee={employee} />
          ))}
        </Paper>

        <Paper
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mb: 1.5 }}>
            Bench (Under-allocated)
          </Typography>

          {bench.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Everyone is allocated</Typography>
          )}

          {bench.map((employee) => (
            <EmployeeRow key={employee.employee_id} employee={employee} />
          ))}
        </Paper>
      </Box>
    </Box>
  );
}

export default CapacityOverviewReport;
